import {
  CASE_FILE_STATUS,
  CaseDocumentFileModel,
  CaseDocumentModel,
  CaseMedicalField,
  CaseMedicalListField,
  CaseModel,
  MEDICAL_LIST_FIELDS,
} from "@shared/models";

export const UPLOAD_FAILED_MESSAGE = "Upload failed. Try again.";

export const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/** E.g. "A, B and C". */
export const joinWithAnd = (items: string[]) =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : (items[0] ?? "");

/**
 * Whether a parsing saved nothing because every file it read failed. Only those files count: when a replacement
 * fails, the files it replaced are put back in the document.
 */
export const hasParsingFailed = ({ files }: CaseDocumentModel, parsedFiles: CaseDocumentFileModel[]) =>
  files
    .filter(({ id }) => parsedFiles.some((parsedFile) => parsedFile.id === id))
    .every(({ status }) => status === CASE_FILE_STATUS.FAILED);

/** Empty when the case has no subject name. */
export const getSubjectName = (caseObj: CaseModel | null) => {
  const { first_name, last_name } = caseObj?.case_person_details ?? {};

  return [first_name, last_name].filter(Boolean).join(" ");
};

/** E.g. "Medical summaries parsed · 2 values to choose · 1 flagged". */
export const getMedicalSummariesParsedMessage = ({ pending_choices, flagged_fields }: CaseDocumentModel) =>
  [
    "Medical summaries parsed",
    pending_choices && `${pluralize(pending_choices, "value")} to choose`,
    flagged_fields && `${flagged_fields} flagged`,
  ]
    .filter(Boolean)
    .join(" · ");

export const isMedicalListField = (field: string): field is CaseMedicalListField =>
  MEDICAL_LIST_FIELDS.includes(field as CaseMedicalListField);

export const hasMedicalFieldText = (caseObj: CaseModel | null, field: CaseMedicalField) => {
  const value = caseObj?.[field];
  return Array.isArray(value) ? value.length > 0 : !!value;
};

/** A list field's text box holds one item per line. */
export const listItemsToText = (items?: string[] | null) => (items ?? []).join("\n");

export const textToListItems = (text?: string | null) =>
  (text ?? "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
